import React from "react";
import { useTranslation } from "react-i18next";
import U2000ActionBtn from "../../../../u2000Core/components/buttons/U2000ActionBtn";
import U2000ResponsiveToolbar from "../../../../u2000Core/components/toolbar/U2000ResponsiveToolbar";
import { FaEye, FaPlus, FaRegFile, FaTrashAlt } from "react-icons/fa";
import { useU2000AuthContext } from "../../../../u2000Core/context/auth/U2000UseAuthContext";
import type { U2000FormStateHandlers } from "../../../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import { U2000FormStateEnumState } from "../../../../u2000Core/reducer/formReducer/U2000FormState";
import { NS, U3148ROLES } from "../../../U3148";
import { TbLockPassword } from "react-icons/tb";

// Typage des handlers spécifiques au formulaire d'affiliation
interface U3148AffiliationFormStateHandlers extends U2000FormStateHandlers<any> {
  getData: () => any;
  getFormState: () => U2000FormStateEnumState;
  importUser: () => void;
  addNewUser: () => void;
  modifyUser: () => void;
  deleteUser: () => void;
}

// Typage des props du composant
interface U3148AffiliationFormBtnsComponentProps {
  formStateHandlers: U3148AffiliationFormStateHandlers;
}

// Composant principal pour la barre d'outils du formulaire d'affiliation
export const U3148AffiliationFormToolBar: React.FC<U3148AffiliationFormBtnsComponentProps> = ({ formStateHandlers }) => {
  const formState = formStateHandlers.getFormState();
  const ssoEnabled = formStateHandlers.getData()?.user?.ssoEnabled === true;

  const { hasRole } = useU2000AuthContext();
  const { t } = useTranslation();

  const actions = [
    {
      key: "import",
      label: t("btnImportAffiliation", { ns: NS }),
      hasRole: hasRole(U3148ROLES.USERS),
      disabled: formState === U2000FormStateEnumState.EDIT,
      handleClick: () => formStateHandlers.importUser(),
      icon: FaPlus,
      Component: U2000ActionBtn,
    },
    {
      key: "add",
      label: t("btnAdd", { ns: NS }),
      hasRole: hasRole(U3148ROLES.USERS),
      handleClick: () => {
        formStateHandlers.addNewUser();
      },
      disabled: formState === U2000FormStateEnumState.EDIT,
      icon: FaPlus,
      Component: U2000ActionBtn,
    },
    {
      key: "modify",
      label: t("btnModify", { ns: NS }),
      hasRole: hasRole(U3148ROLES.USERS),
      handleClick: () => {
        formStateHandlers.modifyUser();
      },
      disabled: formState === U2000FormStateEnumState.EDIT,
      icon: FaRegFile,
      Component: U2000ActionBtn,
    },
    {
      key: "delete",
      label: t("btnDelete", { ns: NS }),
      hasRole: hasRole(U3148ROLES.USERS),
      handleClick: () => {
        formStateHandlers.deleteUser();
      },
      disabled: formState === U2000FormStateEnumState.EDIT,
      icon: FaTrashAlt,
      Component: U2000ActionBtn,
    },
    {
      key: "resetPwd",
      label: t("btnResetPwd", { ns: NS }),
      hasRole: hasRole(U3148ROLES.USERS) && !ssoEnabled,
      handleClick: () => {
        formStateHandlers.resetPassword();
      },
      disabled: formStateHandlers.getFormState() === U2000FormStateEnumState.EDIT || formStateHandlers.getData()?.user?.active === false,
      icon: TbLockPassword,
      Component: U2000ActionBtn,
    },
    {
      key: "showSecretKey",
      label: t("btnShowSecretKey", { ns: NS }),
      handleClick: () => {
        formStateHandlers.showSecretKey();
      },
      hasRole: hasRole(U3148ROLES.USERS) && !ssoEnabled,
      disabled: formStateHandlers.getFormState() === U2000FormStateEnumState.EDIT || formStateHandlers.getData()?.active === false,
      icon: FaEye,
      Component: U2000ActionBtn,
    },
  ];

  return <U2000ResponsiveToolbar actions={actions} />;
};
